import {
  DeleteOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Button, Col, Form, Input, InputNumber, Row, Space, message } from 'antd';
import axios from 'axios';
import { useContext, useEffect } from 'react';
import { BsImages, BsUpcScan } from 'react-icons/bs';

import { clearObjFields } from '../../utils';
import { formatThousands, parseThousands } from '../../utils/numberInput';
import FormFooter from './components/FormFooter';
import SectionCard from './components/SectionCard';
import ImageUpload from './ImageUpload';
import { LabelContext } from './ProductDataContext';
import SingleImageUpload from './SingleImageUpload';
import { nonNegativeRule } from '../../utils/formRules';

/** Талбарууд картын бүтэн өргөнөөр сунахгүй — зүүн талд багцлагдана */
const FORM_MAX_WIDTH = 960;

/**
 * Тоон талбар. Утгыг ЗОРИУДААР засварладаггүй — өмнө нь `min` болон "-"-г хаах
 * `onKeyDown` нь буруу утгыг чимээгүй өөрчилдөг тул хэрэглэгч бичсэнээсээ өөр
 * утга хадгалагдсаныг мэдэхгүй үлддэг байсан. Шалгуурыг дүрмүүд хийж, алдааг ил
 * харуулна.
 */
const numberFieldProps = {
  style: { width: '100%' },
};

const ProductVersion = ({ current, steps, next, prev }) => {
  const productContext = useContext(LabelContext);
  const variantList = productContext.variantList.prop;
  const sender = productContext.labelInfo.productItem;

  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue({ price: sender.price });

    /**
     * Энэ алхмаас гарахад (Буцах товч, browser-ийн back, алхмын гарчиг дээр
     * дарах) бөглөсөн хувилбаруудыг контекстэд хадгална — эргэж ирэхэд утга нь
     * сэргэнэ.
     */
    return () => productContext.onChangeVariantList(form.getFieldValue('productList'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFinish = ({ productList }) => {
    productContext.onChangeVariantList(productList);
    next();
  };

  const onBack = () => {
    const list = form.getFieldValue('productList');
    productContext.onChangeVariantList(list);
    prev();
  };

  const generateSKU = (fieldName) => {
    let list = form.getFieldValue('productList');
    const color = list[fieldName]?.color;
    const size = list[fieldName]?.size;
    const type = list[fieldName]?.type;

    if (checkRequiredFieldsSku(color, size, type)) {
      let body = {
        baseSku: productContext?.productBaseSku,
        color: color ?? '',
        size: size ?? '',
        type: type ?? '',
        productSkus:
          list?.length > 0
            ? list.filter((el) => el?.productSKU).map((item) => item.productSKU)
            : [],
      };

      axios
        .post('/api/v1.0/product/variantsku', clearObjFields(body))
        .then(({ data }) => {
          if (data?.statusCode === 200) {
            list[fieldName].productSKU = data?.productSku;
            form.setFieldsValue({ productList: list });
          }
        })
        .catch((err) => {
          console.error('Variant SKU generate error: ', err);
        });
    }
  };

  const checkRequiredFieldsSku = (color, size, type) => {
    const list = form.getFieldValue('productList');
    let requiredLabels = [];

    if (Boolean(list?.find((el) => el?.color)) && !Boolean(color)) {
      requiredLabels.push('Өнгө');
    }

    if (Boolean(list?.find((el) => el?.size)) && !Boolean(size)) {
      requiredLabels.push('Хэмжээ');
    }

    if (Boolean(list?.find((el) => el?.type)) && !Boolean(type)) {
      requiredLabels.push('Төрөл');
    }

    if (requiredLabels.length > 0) {
      message.error(`${requiredLabels.join(', ')} дутуу байна.`, 5);
      return false;
    }

    if (!color && !size && !type) {
      message.error('Өнгө, хэмжээ, төрлийн аль нэгийг бөглөсөн байх ёстой.', 5);
      return false;
    }

    return true;
  };

  return (
    <Form
      form={form}
      initialValues={{
        productList: variantList ?? [
          {
            color: '',
            size: '',
            type: '',
            productSKU: '',
            price: '',
            stock: '',
            image: undefined,
            images: [],
          },
        ],
        barcodes: sender.barcodes,
      }}
      layout="vertical"
      onFinish={onFinish}
      /**
       * Бөглөж байх зуур нь контекстэд хадгална — refresh хийхэд хувилбарууд
       * сэргэнэ (контекст нь sessionStorage-д draft болгож бичдэг).
       */
      onValuesChange={(_, allValues) => productContext.onChangeVariantList(allValues.productList)}
    >
      <Form.List name="productList">
        {(fields, { add, remove }) => (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {fields.map((field) => {
              const list = form.getFieldValue('productList');

              return (
                <SectionCard
                  key={field.key}
                  icon={<BsImages />}
                  title={`Хувилбар ${field.name + 1}`}
                  action={
                    field.name > 0 ? (
                      <Button
                        danger
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => remove(field.name)}
                      />
                    ) : null
                  }
                >
                  <Row gutter={16} style={{ maxWidth: FORM_MAX_WIDTH }}>
                    <Col>
                      <Form.Item
                        {...field}
                        name={[field.name, 'image']}
                        label="Үндсэн зураг /5MB доош/"
                        rules={[{ required: true, message: 'Зураг оруулна уу!' }]}
                      >
                        <SingleImageUpload list={list?.[field?.name]?.image} />
                      </Form.Item>
                    </Col>

                    <Col flex="auto">
                      <Form.Item
                        {...field}
                        name={[field.name, 'images']}
                        label="Бусад зураг /5MB доош/"
                      >
                        <ImageUpload />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} lg={6}>
                      <Form.Item
                        {...field}
                        label="Өнгө"
                        name={[field.name, 'color']}
                        rules={[
                          {
                            required: Boolean(list?.find((el) => el?.color)),
                            message: 'Өнгө дутуу байна!',
                          },
                        ]}
                      >
                        <Input placeholder="Энд бич" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} lg={6}>
                      <Form.Item
                        {...field}
                        label="Хэмжээ"
                        name={[field.name, 'size']}
                        rules={[
                          {
                            required: Boolean(list?.find((el) => el?.size)),
                            message: 'Хэмжээ дутуу байна!',
                          },
                        ]}
                      >
                        <Input placeholder="Энд бич" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} lg={6}>
                      <Form.Item
                        {...field}
                        label="Төрөл"
                        name={[field.name, 'type']}
                        rules={[
                          {
                            required: Boolean(list?.find((el) => el?.type)),
                            message: 'Төрөл дутуу байна!',
                          },
                        ]}
                      >
                        <Input placeholder="Энд бич" />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} lg={6}>
                      <Form.Item
                        {...field}
                        label="Зарах үнэ"
                        name={[field.name, 'price']}
                        rules={[
                          { required: true, message: 'Үнэ дутуу байна!' },
                          nonNegativeRule('Зарах үнэ'),
                        ]}
                      >
                        <InputNumber
                          {...numberFieldProps}
                          placeholder="Энд бич"
                          formatter={formatThousands}
                          parser={parseThousands}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} lg={8}>
                      <Form.Item label="SKU" required>
                        <Space.Compact style={{ width: '100%' }}>
                          <Form.Item
                            {...field}
                            noStyle
                            name={[field.name, 'productSKU']}
                            rules={[{ required: true, message: 'SKU дутуу байна!' }]}
                          >
                            <Input disabled placeholder="Код үүсгэх" />
                          </Form.Item>
                          <Button
                            type="primary"
                            icon={<ReloadOutlined />}
                            onClick={() => generateSKU(field.name)}
                            title="SKU код үүсгэх"
                          />
                        </Space.Compact>
                      </Form.Item>
                    </Col>

                    <Form.List {...field} name={[field.name, 'barcode']}>
                      {(barcodeFields, { add: addBarcode, remove: removeBarcode }) => (
                        <>
                          {barcodeFields.map(({ key, name, ...restField }) => (
                            <Col key={key} xs={24} sm={12} lg={8}>
                              <Form.Item
                                {...restField}
                                label={`Код ${name + 1}`}
                                name={name}
                                rules={[{ required: true, message: 'Код оруулна уу!' }]}
                              >
                                <Input
                                  placeholder="Код оруулна уу"
                                  suffix={
                                    <MinusCircleOutlined
                                      onClick={() => removeBarcode(name)}
                                      style={{ color: '#8993a5', cursor: 'pointer' }}
                                    />
                                  }
                                />
                              </Form.Item>
                            </Col>
                          ))}

                          <Col xs={24} sm={12} lg={8}>
                            {/* Талбартай нэг эгнээнд байрлуулахын тулд хоосон label үлдээв */}
                            <Form.Item
                              label={barcodeFields.length ? ' ' : 'Код'}
                              style={{ marginBottom: 0 }}
                            >
                              <Button
                                onClick={() => addBarcode()}
                                block
                                icon={<PlusOutlined />}
                                style={{ color: '#4BBEDA', borderColor: '#4BBEDA' }}
                              >
                                Код нэмэх
                              </Button>
                            </Form.Item>
                          </Col>
                        </>
                      )}
                    </Form.List>
                  </Row>
                </SectionCard>
              );
            })}

            <SectionCard icon={<BsUpcScan />} title="Шинэ хувилбар">
              <Button
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
                style={{ color: '#4BBEDA', borderColor: '#4BBEDA', height: 48 }}
              >
                Хувилбар нэмэх
              </Button>
            </SectionCard>

            <FormFooter>
              {current > 0 && <Button onClick={onBack}>Буцах</Button>}
              {current < steps.length - 1 && (
                <Button type="primary" htmlType="submit">
                  Үргэлжлүүлэх
                </Button>
              )}
            </FormFooter>
          </Space>
        )}
      </Form.List>
    </Form>
  );
};

export default ProductVersion;
